import React from "react";
import { View, StyleSheet, SafeAreaView, ScrollView, Image, Linking } from "react-native";
import { Button, fontWeights, Text, theme, typography } from "ui-components";
import { Grid, Container, Row, Column } from "ui-grid";
import { Stack } from "../../components/layout/stack";
import { Spacer } from "../../components/spacer/spacer";
import { useNavigation } from "@react-navigation/native";
import { useConfig } from "../../hooks/useConfig";
import { useSelector } from "react-redux";
import { toHumanDollars } from "../../utils/money";
import { RootState } from "../../redux/reducers";
import { HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { translate } from "../../i18n";
import automationTestingLocators from "./utils/automation-testing-locators.json";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import { BottomNav } from "../../components/layout/bottom-nav";
import { Footer } from "../../components/new/footer/footer";

const MONEYGRAM_SECURE_LINK = "https://secure.moneygram.com/locations";
const MONEYGRAM_START_LINK = "https://start.moneygram.com";

const PaymentFlowPaymentMethodMoneyGram = (props: HomeScreenProps) => {
  const navigation = useNavigation();
  const { params } = props?.route;
  const { amounts, account_number: reservationAccountNumber } = params;
  const config = useConfig();
  const propertyFees = useSelector((state: RootState) => state.reservation.propertyFees);
  const MONEYGRAM_FEE = Number(propertyFees?.moneygram?.fee);
  const logger = useSelector((state: RootState) => state.logger);

  const done = () => {
    return navigation.navigate("Home", { ...params });
  };

  const numberAmount = Number(amounts?.amount || 0);
  const formattedAmount = toHumanDollars(numberAmount);
  const formattedFee = toHumanDollars(MONEYGRAM_FEE);
  const formattedTotal = toHumanDollars(numberAmount + MONEYGRAM_FEE);

  const RenderSeparator = ({ spaceTop, spaceBottom }: any) => {
    return (
      <Grid>
        <Container>
          <Row>
            <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            <Column
              span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
              style={[styles.inputRow, { paddingLeft: 0, paddingRight: 0 }]}>
              <Spacer size={spaceTop} />
              <View
                style={{
                  borderColor: theme.colors.stroke,
                  borderTopWidth: 0.8,
                }}
              />
              <Spacer size={spaceBottom} />
            </Column>
            <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
          </Row>
        </Container>
      </Grid>
    );
  };
  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollableArea}>
        <View style={{ flex: 1 }}>
          <Spacer size="medium" />
          <Grid>
            <Container>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column
                  span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
                  style={[styles.inputRow, styles.header]}>
                  <View style={styles.inputRowInner}>
                    <Image
                      source={require("../../../assets/moneygram-new.png")}
                      style={{ height: 40, width: 191 }}
                      resizeMode={"contain"}
                      {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                        .moneygramImage}
                    />
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                  <View style={styles.inputRowInner}>
                    <Text
                      style={styles.tagLine}
                      {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                        .paperlessMoneygramText}>
                      {translate("paymentFlow.paymentMethodMoneygram.paperlessMoneygram")}
                    </Text>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            </Container>
          </Grid>
          <RenderSeparator spaceTop={"medium"} spaceBottom={"medium"} />
          <Grid>
            <Container>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <Stack space="small">
                    <View style={styles.inputRowInner}>
                      <Text
                        style={styles.paymentDetailsLeftText}
                        {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                          .accountNumberText}>
                        {translate("paymentFlow.paymentMethodMoneygram.accountNumber")}
                      </Text>
                      <Text
                        style={[styles.paymentDetailsRightText]}
                        {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                          .accountNumber}>
                        {reservationAccountNumber}
                      </Text>
                    </View>
                    <View style={styles.inputRowInner}>
                      <Text
                        style={styles.paymentDetailsLeftText}
                        {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                          .receiveCodeText}>
                        {translate("paymentFlow.paymentMethodMoneygram.receiveCode")}
                      </Text>
                      <Text
                        style={[styles.paymentDetailsRightText]}
                        {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                          .receiveCode}>
                        {config.moneygramReservationCode}
                      </Text>
                    </View>
                    <View style={styles.inputRowInner}>
                      <Text
                        style={styles.paymentDetailsLeftText}
                        {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                          .identificationText}>
                        {translate("paymentFlow.paymentMethodMoneygram.identification")}
                      </Text>
                      <Text
                        style={[styles.paymentDetailsRightText]}
                        {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                          .identification}>
                        {"Driver’s License or other form of ID"}
                      </Text>
                    </View>
                    <View style={styles.inputRowInner}>
                      <Text
                        style={styles.paymentDetailsLeftText}
                        {...automationTestingLocators.paymentFlowPaymentMethodMoneygram.amountText}>
                        {translate("paymentFlow.paymentMethodMoneygram.amount")}
                      </Text>
                      <Text
                        style={[styles.paymentDetailsRightText]}
                        {...automationTestingLocators.paymentFlowPaymentMethodMoneygram.amount}>
                        {formattedAmount}
                      </Text>
                    </View>
                    <View style={styles.inputRowInner}>
                      <Text
                        style={styles.paymentDetailsLeftText}
                        {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                          .platformFeeText}>
                        {translate("paymentFlow.paymentMethodMoneygram.platformFees")}
                      </Text>
                      <Text
                        style={[styles.paymentDetailsRightText]}
                        {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                          .platformFee}>
                        {formattedFee}
                      </Text>
                    </View>
                  </Stack>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            </Container>
          </Grid>
          <RenderSeparator spaceTop={"medium"} spaceBottom={"small"} />
          <Grid>
            <Container>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <View style={styles.inputRowInner}>
                    <Text
                      style={styles.paymentDetailsLeftText}
                      {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                        .cashBalanceDueText}>
                      {translate("paymentFlow.paymentMethodMoneygram.cashBalanceDue")}
                    </Text>
                    <Text
                      style={[styles.rowRightTitle]}
                      {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                        .cashBalanceDue}>
                      {formattedTotal}
                    </Text>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            </Container>
          </Grid>
          <RenderSeparator spaceTop={"small"} spaceBottom={"large"} />
          <Grid>
            <Container>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                  <View style={[styles.inputRowInner]}>
                    <View style={styles.instructionCard}>
                      <Text
                        style={styles.instructionHeading}
                        {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                          .instructionHeading}>
                        {translate("paymentFlow.paymentMethodMoneygram.followInstruction")}
                      </Text>
                      <Text
                        style={styles.instructionPoints}
                        {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                          .instructionPoint1}>
                        {translate("paymentFlow.paymentMethodMoneygram.instruction1")}
                      </Text>
                    </View>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            </Container>
          </Grid>
          <Grid>
            <Container>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column
                  span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
                  style={[styles.inputRow, { paddingBottom: 50 }]}>
                  <View />
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            </Container>
          </Grid>
        </View>
      </ScrollView>
      <BottomNav backgroundColor={theme.colors.white}>
        <Stack space="small">
          <Row>
            <Column span={{ xs: false, sm: false, md: 1.5, lg: 2.5 }} />
            <Column style={{ paddingVertical: 2 }} span={{ xs: 12, sm: 12, md: 9, lg: 7 }}>
              <Button
                text={
                  translate("paymentFlow.paymentMethodMoneygram.findMoneygramLocation") as string
                }
                onPress={() => Linking.openURL(MONEYGRAM_SECURE_LINK)}
                type={"secondary"}
                {...automationTestingLocators.paymentFlowPaymentMethodMoneygram.findLocationButton}
              />
            </Column>
            <Column span={{ xs: false, sm: false, md: 1.5, lg: 2.5 }} />
          </Row>
          <Row>
            <Column span={{ xs: false, sm: false, md: 1.5, lg: 2.5 }} />
            <Column style={{ paddingVertical: 2 }} span={{ xs: 12, sm: 12, md: 9, lg: 7 }}>
              <Button
                text={translate("paymentFlow.paymentMethodMoneygram.goBackToHomePage") as string}
                onPress={done}
                {...automationTestingLocators.paymentFlowPaymentMethodMoneygram
                  .backToHomePageButton}
              />
            </Column>
            <Column span={{ xs: false, sm: false, md: 1.5, lg: 2.5 }} />
          </Row>
        </Stack>
      </BottomNav>
      <Footer />
    </SafeAreaView>
  );
};
const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...domusoResponsiveStyleHelper({
      webPhone: {
        backgroundColor: theme.colors.white,
      },
      webTablet: {
        backgroundColor: "#FAFAFA",
      },
      webDesktop: {
        backgroundColor: "#FAFAFA",
      },
      iosPhone: {
        backgroundColor: theme.colors.white,
      },
      iosTablet: {
        backgroundColor: "#FAFAFA",
      },
      androidPhone: {
        backgroundColor: theme.colors.white,
      },
      androidTablet: {
        backgroundColor: "#FAFAFA",
      },
    }),
  },
  scrollView: { flexGrow: 0 },
  scrollableArea: {
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: theme.colors.white, paddingBottom: 10 },
      webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      iosPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
      iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      androidPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
      androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
    }),
  },
  inputRow: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 30,
        paddingRight: 30,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 30,
        paddingRight: 30,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 30,
        paddingRight: 30,
        backgroundColor: "white",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRowInner: {
    flexDirection: "row",
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 10,
        paddingRight: 10,
      },
      webDesktop: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 10,
        paddingRight: 10,
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 10,
        paddingRight: 10,
      },
    }),
  },
  instructionImageContainer: {
    borderWidth: 1,
    padding: 8,
    borderRadius: 10,
    borderColor: theme.colors.stroke,
    height: 38,
    width: 191,
  },
  paymentDetailsTextView: {
    flexDirection: "row",
  },
  paymentDetailsLeftText: {
    ...typography.title2,
    color: theme.colors.text4,
    fontWeight: fontWeights.Regular,
    flex: 0.5,
  },
  paymentDetailsRightText: {
    ...typography.title2,
    color: theme.colors.text3,
    textAlign: "right",
    marginLeft: 10,
    flex: 0.5,
  },
  rowRightTitle: {
    ...typography.title2,
    color: theme.colors.secondary,
    textAlign: "right",
    marginLeft: 10,
    flex: 0.5,
    fontFamily: "Lato_700Bold",
  },
  tagLine: {
    ...typography.title2,
    color: theme.colors.text3,
  },
  policyText: {
    ...typography.title2,
    color: theme.colors.text3,
    marginLeft: 10,
    flex: 1,
  },
  alertText: {
    ...typography.body,
    color: theme.colors.alert,
    marginLeft: 10,
    flex: 1,
  },
  checkboxStyle: {
    width: 20,
    height: 20,
  },
  dividerPaymentMethods: {
    margin: 6,
  },
  instructionCard: {
    borderWidth: 1,
    padding: 16,
    borderRadius: 10,
    borderColor: theme.colors.stroke,
    justifyContent: "center",
    flex: 1,
  },
  instructionHeading: {
    ...typography.subHeading,
    color: theme.colors.text3,
    fontFamily: "Lato_700Bold",
  },
  instructionSubHeading: {
    ...typography.subHeading,
    color: theme.colors.text3,
  },
  instructionPoints: {
    ...typography.body,
    color: theme.colors.text2,
    fontFamily: "Lato_400Regular",
  },
  header: {
    ...domusoResponsiveStyleHelper({
      webPhone: { paddingVertical: 3 },
      webTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
      webDesktop: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
      iosPhone: { paddingVertical: 3 },
      iosTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
      androidPhone: { paddingVertical: 3 },
      androidTablet: {
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",

        background: "#FFFFFF",

        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        backgroundColor: "white",
      },
    }),
  },
});
export default PaymentFlowPaymentMethodMoneyGram;
